import React from "react";
import { Box, Button, Typography } from "@mui/material";
import { useSelector } from "react-redux";
import { IStoreState } from "../../redux/initialStoreState";
import { LoadState } from "../../constants/enums";
import TimelineEntry from "./TimelineEntry";

interface TimelineListProps {
  onRetry: () => void;
}

/**
 * TimelineList renders the vertical timeline of change history entries.
 * - Reads changeHistory state from Redux
 * - Sorts entries by create_ts in reverse chronological order (newest first)
 * - Renders a vertical connector line on the left side
 * - Displays empty state or error state as appropriate
 */
const TimelineList: React.FC<TimelineListProps> = ({ onRetry }) => {
  const { list, loading, error } = useSelector(
    (state: IStoreState) => state.changeHistory
  );

  // Error state
  if (error && loading === LoadState.Failed) {
    return (
      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          justifyContent: "center",
          py: 6,
        }}
      >
        <Typography variant="body1" color="error" sx={{ mb: 2 }}>
          {error}
        </Typography>
        <Button variant="outlined" color="primary" onClick={onRetry}>
          Retry
        </Button>
      </Box>
    );
  }

  // Empty state (only show when loading is complete)
  if (list.length === 0 && loading === LoadState.Loaded) {
    return (
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          py: 6,
        }}
      >
        <Typography variant="body1" color="text.secondary">
          No change history available
        </Typography>
      </Box>
    );
  }

  // Sort entries by create_ts in reverse chronological order (newest first)
  const sortedEntries = [...list].sort(
    (a, b) => new Date(b.create_ts).getTime() - new Date(a.create_ts).getTime()
  );

  return (
    <Box sx={{ position: "relative", pl: 3, py: 2 }}>
      {/* Vertical connector line */}
      <Box
        sx={{
          position: "absolute",
          left: 8,
          top: 0,
          bottom: 0,
          width: "2px",
          backgroundColor: "divider",
        }}
      />

      {/* Timeline entries */}
      {sortedEntries.map((record) => (
        <TimelineEntry key={record.change_history_id} record={record} />
      ))}
    </Box>
  );
};

export default TimelineList;
